import { useState, useEffect, useRef } from "react";
import { Plus, Car, User, FileText, Trash2, ChevronRight, UserCheck, Download, CarFront, Pen, Search, Calendar, ArrowRight, Loader2, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import CustomerVehicleSelector from "../components/CustomerVehicleSelector";
import SignaturePad from "../components/SignaturePad";
import VehicleDamageMarker, { getDamageDescription } from "../components/VehicleDamageMarker";
import { generateAcceptancePDF } from "../utils/pdfGenerator";
import TemparioSelector from "../components/TemparioSelector";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VehicleAcceptance({ user }) {
  const [acceptances, setAcceptances] = useState([]);
  const [filteredAcceptances, setFilteredAcceptances] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedAcceptance, setSelectedAcceptance] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [damageData, setDamageData] = useState({ marks: [] });
  const [externalPhotos, setExternalPhotos] = useState([]);
  const [internalPhotos, setInternalPhotos] = useState([]);
  const damageCanvasRef = useRef(null);
  const viewDamageCanvasRef = useRef(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    customer_fiscal_code: "",
    vehicle_id: "",
    vehicle_plate: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_version: "",
    vehicle_year: "",
    vehicle_vin: "",
    vehicle_engine_code: "",
    vehicle_km: "",
    vehicle_fuel_type: "",
    vehicle_cubic_capacity: "",
    vehicle_power_kw: "",
    vehicle_power_hp: "",
    vehicle_first_registration: "",
    fuel_level: "1/2",
    external_condition: "",
    internal_condition: "",
    reported_issues: "",
    accessories: [],
    notes: "",
    authorize_test_drive: false,
    selected_operations: []  // Operazioni selezionate dal tempario
  });

  // Stato per ore manodopera totali dal tempario
  const [totalLaborHours, setTotalLaborHours] = useState(0);

  const accessoriesList = [
    "Ruota di scorta",
    "Kit attrezzi",
    "Triangolo",
    "Giubbotto catarifrangente",
    "Documenti",
    "Chiavi",
    "Telecomando",
    "Tappetini",
    "Antenna"
  ];

  const fuelLevels = ["Vuoto", "1/4", "1/2", "3/4", "Pieno"];

  useEffect(() => {
    fetchData();
  }, []);

  // Filter acceptances based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredAcceptances(acceptances);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = acceptances.filter(acc => 
        acc.acceptance_number?.toLowerCase().includes(term) ||
        acc.customer_name?.toLowerCase().includes(term) ||
        acc.vehicle_plate?.toLowerCase().includes(term) ||
        acc.vehicle_brand?.toLowerCase().includes(term) ||
        acc.vehicle_model?.toLowerCase().includes(term)
      );
      setFilteredAcceptances(filtered);
    }
  }, [searchTerm, acceptances]);

  const fetchData = async () => {
    try {
      const [acceptancesRes, customersRes, appointmentsRes] = await Promise.all([
        authFetch(`${API}/acceptances`, { }),
        authFetch(`${API}/customers`, { }),
        authFetch(`${API}/appointments`, { })
      ]);
      if (acceptancesRes.ok) {
        const data = await acceptancesRes.json();
        setAcceptances(data);
        setFilteredAcceptances(data);
      }
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (appointmentsRes.ok) {
        const appts = await appointmentsRes.json();
        // Filter only today's and confirmed appointments
        const today = new Date().toISOString().split("T")[0];
        const todayAppts = appts.filter(a => 
          a.appointment_date === today && a.status !== "completed" && a.status !== "cancelled"
        );
        setAppointments(todayAppts);
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  // Import data from appointment
  const importFromAppointment = (apt) => {
    setFormData(prev => ({
      ...prev,
      customer_id: apt.customer_id || "",
      customer_name: apt.customer_name || "",
      customer_email: apt.customer_email || "",
      customer_phone: apt.customer_phone || "",
      customer_address: apt.customer_address || "",
      customer_fiscal_code: apt.customer_fiscal_code || "",
      vehicle_plate: apt.vehicle_plate || "",
      vehicle_brand: apt.vehicle_brand || "",
      vehicle_model: apt.vehicle_model || "",
      vehicle_version: apt.vehicle_version || "",
      vehicle_year: apt.vehicle_year || "",
      vehicle_vin: apt.vehicle_vin || "",
      vehicle_fuel_type: apt.vehicle_fuel_type || "",
      reported_issues: apt.notes || ""
    }));
    setSelectedCustomerId(apt.customer_id || "");
    setShowImportDialog(false);
    setShowDialog(true);
    toast.success(`Dati importati da appuntamento: ${apt.customer_name}`);
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        customer_address: customer.address,
        customer_fiscal_code: customer.fiscal_code
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        customer_id: selectedCustomerId || null,
        vehicle_km: parseInt(formData.vehicle_km) || 0,
        vehicle_year: parseInt(formData.vehicle_year) || new Date().getFullYear(),
        damage_data: damageData
      };

      const response = await authFetch(`${API}/acceptances`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        toast.success("Scheda accettazione creata");
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Errore");
      }
    } catch (error) {
      toast.error(error.message || "Errore nel salvataggio");
    }
  };

  const handleStatusChange = async (acceptanceId, status) => {
    try {
      const response = await authFetch(`${API}/acceptances/${acceptanceId}/status?status=${status}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const resetForm = () => {
    setSelectedCustomerId("");
    setExternalPhotos([]);
    setInternalPhotos([]);
    setFormData({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      customer_address: "",
      customer_fiscal_code: "",
      vehicle_id: "",
      vehicle_plate: "",
      vehicle_brand: "",
      vehicle_model: "",
      vehicle_version: "",
      vehicle_year: "",
      vehicle_vin: "",
      vehicle_engine_code: "",
      vehicle_km: "",
      vehicle_fuel_type: "",
      vehicle_cubic_capacity: "",
      vehicle_power_kw: "",
      vehicle_power_hp: "",
      vehicle_first_registration: "",
      fuel_level: "1/2",
      external_condition: "",
      internal_condition: "",
      reported_issues: "",
      accessories: [],
      notes: "",
      authorize_test_drive: false
    });
    setDamageData({ marks: [] });
  };

  const toggleAccessory = (accessory) => {
    setFormData(prev => ({
      ...prev,
      accessories: (prev.accessories || []).includes(accessory)
        ? (prev.accessories || []).filter(a => a !== accessory)
        : [...(prev.accessories || []), accessory]
    }));
  };

  // Generate PDF with professional design and damage diagram
  const generatePDF = async (acceptance, signature = null) => {
    // Get damage image from canvas if there are marks
    let damageImageData = null;
    if (acceptance.damage_data?.marks && acceptance.damage_data.marks.length > 0) {
      // Use viewDamageCanvasRef for viewing existing acceptances, damageCanvasRef for new ones
      const canvasRef = viewDamageCanvasRef.current || damageCanvasRef.current;
      if (canvasRef) {
        try {
          // Wait a bit for canvas to render
          await new Promise(resolve => setTimeout(resolve, 100));
          damageImageData = canvasRef.toDataURL('image/png');
        } catch (e) {
          console.error("Error getting damage canvas image:", e);
        }
      }
    }
    
    const doc = await generateAcceptancePDF(acceptance, user, signature || signatureData, damageImageData);
    doc.save(`Accettazione_${acceptance.vehicle_plate}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF scaricato");
  };

  // Delete acceptance
  const handleDelete = async (acceptanceId) => {
    try {
      const response = await authFetch(`${API}/acceptances/${acceptanceId}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Accettazione eliminata");
        setSelectedAcceptance(null);
        setDeleteConfirm(null);
        fetchData();
      } else {
        throw new Error("Errore");
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Save signature
  const handleSaveSignature = (data) => {
    setSignatureData(data);
    setShowSignaturePad(false);
    toast.success("Firma salvata");
  };

  const statusColors = {
    received: "status-scheduled",
    in_diagnosis: "status-in_progress",
    quote_sent: "status-sent",
    in_repair: "status-in_progress",
    completed: "status-completed",
    delivered: "status-accepted"
  };

  const statusLabels = {
    received: "Ricevuto",
    in_diagnosis: "In Diagnosi",
    quote_sent: "Preventivo Inviato",
    in_repair: "In Riparazione",
    completed: "Completato",
    delivered: "Consegnato"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="acceptances-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Accettazione Veicoli</h1>
        <div className="flex gap-2">
          {/* Import from Appointment Button */}
          {appointments.length > 0 && (
            <Button 
              variant="outline" 
              className="border-primary text-primary hover:bg-primary hover:text-black"
              onClick={() => setShowImportDialog(true)}
              data-testid="import-from-appointment-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Importa da Appuntamento ({appointments.length})
            </Button>
          )}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="new-acceptance-btn" className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Accettazione
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-6xl max-h-[90vh] overflow-y-auto" aria-describedby="acceptance-dialog-description">
              <DialogHeader>
                <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">
                  Scheda Accettazione Veicolo
                </DialogTitle>
                <p id="acceptance-dialog-description" className="sr-only">Form per creare una nuova scheda di accettazione veicolo</p>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {/* Customer Selection from Registry */}
                {customers.length > 0 && (
                  <div className="form-group">
                    <label className="form-label flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-primary" />
                      Seleziona da Anagrafica
                    </label>
                    <select
                      className="form-input"
                      value={selectedCustomerId}
                      onChange={(e) => {
                        setSelectedCustomerId(e.target.value);
                        handleCustomerSelect(e.target.value);
                      }}
                      data-testid="select-customer-registry"
                    >
                      <option value="">-- Seleziona cliente esistente --</option>
                      {customers.map(c => (
                        <option key={c.customer_id} value={c.customer_id}>
                          {c.name} - {c.fiscal_code}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

              {/* Customer Data */}
              <div>
                <h3 className="text-primary uppercase text-sm font-bold mb-4 flex items-center gap-2">
                  <User className="w-4 h-4" /> Dati Cliente
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Nome e Cognome *</label>
                    <input
                      data-testid="input-acc-customer-name"
                      type="text"
                      className="form-input"
                      value={formData.customer_name}
                      onChange={(e) => setFormData(prev => ({...prev, customer_name: e.target.value}))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice Fiscale *</label>
                    <input
                      data-testid="input-acc-fiscal-code"
                      type="text"
                      className="form-input uppercase"
                      value={formData.customer_fiscal_code}
                      onChange={(e) => setFormData(prev => ({...prev, customer_fiscal_code: e.target.value.toUpperCase()}))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      data-testid="input-acc-email"
                      type="email"
                      className="form-input"
                      value={formData.customer_email}
                      onChange={(e) => setFormData(prev => ({...prev, customer_email: e.target.value}))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Telefono *</label>
                    <input
                      data-testid="input-acc-phone"
                      type="tel"
                      className="form-input"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData(prev => ({...prev, customer_phone: e.target.value}))}
                      required
                    />
                  </div>
                  <div className="form-group col-span-2">
                    <label className="form-label">Indirizzo *</label>
                    <input
                      data-testid="input-acc-address"
                      type="text"
                      className="form-input"
                      value={formData.customer_address}
                      onChange={(e) => setFormData(prev => ({...prev, customer_address: e.target.value}))}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Selection with Customer Vehicle Archive */}
              <CustomerVehicleSelector
                customerId={selectedCustomerId}
                vehicleData={{
                  vehicle_id: formData.vehicle_id,
                  vehicle_plate: formData.vehicle_plate,
                  vehicle_brand: formData.vehicle_brand,
                  vehicle_model: formData.vehicle_model,
                  vehicle_version: formData.vehicle_version,
                  vehicle_year: formData.vehicle_year,
                  vehicle_vin: formData.vehicle_vin,
                  vehicle_engine_code: formData.vehicle_engine_code,
                  vehicle_km: formData.vehicle_km,
                  vehicle_fuel_type: formData.vehicle_fuel_type,
                  vehicle_cubic_capacity: formData.vehicle_cubic_capacity,
                  vehicle_power_kw: formData.vehicle_power_kw,
                  vehicle_power_hp: formData.vehicle_power_hp,
                  vehicle_first_registration: formData.vehicle_first_registration
                }}
                onVehicleChange={(updater) => {
                  if (typeof updater === 'function') {
                    setFormData(prev => ({...prev, ...updater(prev)}));
                  } else {
                    setFormData(prev => ({...prev, ...updater}));
                  }
                }}
                required
              />

              {/* Fuel Level */}
              <div className="form-group">
                <label className="form-label">Livello Carburante</label>
                <select
                  className="form-select"
                  value={formData.fuel_level}
                  onChange={(e) => setFormData(prev => ({...prev, fuel_level: e.target.value}))}
                >
                  {fuelLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              {/* Condition with Photos */}
              <div>
                <h3 className="text-primary uppercase text-sm font-bold mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Problemi Segnalati
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div className="form-group">
                    <label className="form-label">Problemi Segnalati *</label>
                    <textarea
                      data-testid="input-acc-issues"
                      className="form-textarea h-24"
                      value={formData.reported_issues}
                      onChange={(e) => setFormData(prev => ({...prev, reported_issues: e.target.value}))}
                      placeholder="Descrivi i problemi riportati dal cliente..."
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Operazioni da Tempario */}
              <div>
                <h3 className="text-primary uppercase text-sm font-bold mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> 
                  Operazioni Richieste
                  {totalLaborHours > 0 && (
                    <span className="text-blue-400 font-normal text-xs ml-2">
                      Totale: {totalLaborHours}h
                    </span>
                  )}
                </h3>
                <div className="space-y-3">
                  {/* Aggiungi operazione */}
                  <div className="form-group">
                    <label className="form-label text-xs">Cerca nel catalogo/tempario</label>
                    <TemparioSelector
                      value=""
                      onChange={() => {}}
                      onSelectItem={(item) => {
                        // Aggiungi operazione alla lista
                        const newOp = {
                          id: `op_${Date.now()}`,
                          codice: item.codice,
                          descrizione: item.descrizione,
                          ore: item.ore_manodopera || 0,
                          prezzo: item.prezzo || 0,
                          tipo: item.tipo
                        };
                        setFormData(prev => ({
                          ...prev,
                          selected_operations: [...(prev.selected_operations || []), newOp]
                        }));
                        // Aggiorna totale ore
                        setTotalLaborHours(prev => prev + (item.ore_manodopera || 0));
                        toast.success(`Operazione aggiunta: ${item.descrizione}`);
                      }}
                      placeholder="Cerca operazione da aggiungere..."
                      className="w-full"
                    />
                  </div>
                  
                  {/* Lista operazioni selezionate */}
                  {formData.selected_operations?.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formData.selected_operations.map((op, idx) => (
                        <div 
                          key={op.id}
                          className="flex items-center justify-between bg-zinc-800/50 p-2 rounded border border-zinc-700"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                op.tipo === "tempario" ? "bg-blue-500/20 text-blue-400" :
                                op.tipo === "servizio" ? "bg-purple-500/20 text-purple-400" :
                                "bg-green-500/20 text-green-400"
                              }`}>
                                {op.tipo}
                              </span>
                              <span className="text-primary font-mono text-xs">{op.codice}</span>
                            </div>
                            <p className="text-white text-sm">{op.descrizione}</p>
                          </div>
                          <div className="flex items-center gap-3 ml-2">
                            {op.ore > 0 && (
                              <span className="text-blue-400 text-sm flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {op.ore}h
                              </span>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-red-500"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  selected_operations: (prev.selected_operations || []).filter((_, i) => i !== idx)
                                }));
                                setTotalLaborHours(prev => prev - (op.ore || 0));
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Damage Marker */}
              <div>
                <h3 className="text-primary uppercase text-sm font-bold mb-4 flex items-center gap-2">
                  <CarFront className="w-5 h-5" />
                  Mappa Danni Carrozzeria
                </h3>
                <VehicleDamageMarker 
                  damageData={damageData} 
                  setDamageData={(data) => {
                    setDamageData(data);
                    setFormData(prev => ({...prev, damage_marks: data}));
                  }}
                  canvasRef={damageCanvasRef}
                />
              </div>

              {/* Accessories */}
              <div>
                <h3 className="text-primary uppercase text-sm font-bold mb-4">Accessori Presenti</h3>
                <div className="grid grid-cols-3 gap-3">
                  {accessoriesList.map((accessory) => (
                    <div key={accessory} className="flex items-center gap-2">
                      <Checkbox
                        id={accessory}
                        checked={formData.accessories?.includes(accessory) || false}
                        onCheckedChange={() => toggleAccessory(accessory)}
                      />
                      <label htmlFor={accessory} className="text-sm text-zinc-400 cursor-pointer">
                        {accessory}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Test Drive Authorization */}
              <div className="bg-zinc-800/50 rounded-sm p-4 border border-zinc-700">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="authorize_test_drive"
                    checked={formData.authorize_test_drive}
                    onCheckedChange={(checked) => setFormData(prev => ({...prev, authorize_test_drive: checked}))}
                  />
                  <label htmlFor="authorize_test_drive" className="text-white font-medium cursor-pointer flex items-center gap-2">
                    <CarFront className="w-5 h-5 text-primary" />
                    Si autorizza prova su strada
                  </label>
                </div>
                <p className="text-zinc-500 text-xs mt-2 ml-7">
                  Seleziona questa opzione se il cliente autorizza l'officina ad effettuare prove su strada con il veicolo
                </p>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Note Aggiuntive</label>
                <textarea
                  className="form-textarea h-20"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                  placeholder="Altre note..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowDialog(false)} className="text-zinc-400">
                  Annulla
                </Button>
                <Button data-testid="submit-acceptance-btn" type="submit" className="btn-primary">
                  Crea Scheda
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Cerca per targa, cliente, numero accettazione..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="search-acceptances"
        />
      </div>

      {/* List - Table View */}
      {filteredAcceptances.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-xs text-zinc-500 uppercase">
                <th className="py-3 px-4 font-medium">N°</th>
                <th className="py-3 px-4 font-medium">Stato</th>
                <th className="py-3 px-4 font-medium">Targa</th>
                <th className="py-3 px-4 font-medium">Veicolo</th>
                <th className="py-3 px-4 font-medium">Cliente</th>
                <th className="py-3 px-4 font-medium text-right">Km</th>
              </tr>
            </thead>
            <tbody>
              {filteredAcceptances.map((acc) => (
                <tr 
                  key={acc.acceptance_id}
                  className="border-b border-zinc-800/50 hover:bg-zinc-900/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedAcceptance(acc)}
                >
                  <td className="py-3 px-4">
                    <span className="text-primary font-bold">#{acc.acceptance_number || '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs uppercase ${statusColors[acc.status]}`}>
                        {statusLabels[acc.status]}
                      </span>
                      {acc.authorize_test_drive && (
                        <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">
                          Prova OK
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono font-bold text-white">{acc.vehicle_plate}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-300">{acc.vehicle_brand} {acc.vehicle_model}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-400">{acc.customer_name}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <span className="text-zinc-400 font-mono">{(acc.vehicle_km || 0).toLocaleString()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Car className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">{searchTerm ? "Nessun risultato" : "Nessuna accettazione"}</p>
          <p className="empty-state-text">{searchTerm ? "Prova con altri termini di ricerca" : "Crea una nuova scheda di accettazione veicolo"}</p>
        </div>
      )}

      {/* Detail Dialog */}
      {selectedAcceptance && (
        <Dialog open={!!selectedAcceptance} onOpenChange={() => setSelectedAcceptance(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center justify-between">
                <span>Dettaglio Accettazione</span>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowSignaturePad(true)}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700"
                  >
                    <Pen className="w-4 h-4 mr-2" />
                    Firma
                  </Button>
                  <Button
                    onClick={() => generatePDF(selectedAcceptance)}
                    className="btn-primary"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Stampa PDF
                  </Button>
                  <Button
                    onClick={() => setDeleteConfirm(selectedAcceptance.acceptance_id)}
                    variant="outline"
                    size="sm"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            {/* Signature Pad Modal */}
            {showSignaturePad && (
              <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-full max-w-lg">
                  <h3 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] mb-4 text-center">
                    Firma Cliente
                  </h3>
                  <SignaturePad
                    onSave={handleSaveSignature}
                    onCancel={() => setShowSignaturePad(false)}
                    width={400}
                    height={200}
                  />
                </div>
              </div>
            )}
            
            {/* Delete Confirmation */}
            {deleteConfirm && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-4">
                <p className="text-red-400 mb-3">Confermi l'eliminazione di questa accettazione?</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDelete(deleteConfirm)}
                    size="sm"
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Conferma Eliminazione
                  </Button>
                  <Button
                    onClick={() => setDeleteConfirm(null)}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700"
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            )}
            
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-primary font-['JetBrains_Mono']">
                    {selectedAcceptance.vehicle_plate}
                  </div>
                  <div className="text-zinc-400">
                    {selectedAcceptance.vehicle_brand} {selectedAcceptance.vehicle_model} ({selectedAcceptance.vehicle_year})
                  </div>
                </div>
                <Select
                  value={selectedAcceptance.status}
                  onValueChange={(value) => {
                    handleStatusChange(selectedAcceptance.acceptance_id, value);
                    setSelectedAcceptance({...selectedAcceptance, status: value});
                  }}
                >
                  <SelectTrigger className="w-40 bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="received">Ricevuto</SelectItem>
                    <SelectItem value="in_diagnosis">In Diagnosi</SelectItem>
                    <SelectItem value="quote_sent">Preventivo Inviato</SelectItem>
                    <SelectItem value="in_repair">In Riparazione</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                    <SelectItem value="delivered">Consegnato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Signature Preview */}
              {signatureData && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-3">
                  <p className="text-green-400 text-sm mb-2">Firma cliente acquisita</p>
                  <img src={signatureData} alt="Firma" className="h-12 bg-white rounded-sm" />
                </div>
              )}

              {/* Test Drive Badge */}
              {selectedAcceptance.authorize_test_drive && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3 flex items-center gap-2">
                  <CarFront className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-400 font-medium">Cliente autorizza prova su strada</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500">Cliente:</span>
                  <span className="text-white ml-2">{selectedAcceptance.customer_name}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Telefono:</span>
                  <span className="text-white ml-2">{selectedAcceptance.customer_phone}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Email:</span>
                  <span className="text-white ml-2">{selectedAcceptance.customer_email}</span>
                </div>
                <div>
                  <span className="text-zinc-500">CF:</span>
                  <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedAcceptance.customer_fiscal_code}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-zinc-500">Indirizzo:</span>
                  <span className="text-white ml-2">{selectedAcceptance.customer_address}</span>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">VIN:</span>
                    <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedAcceptance.vehicle_vin}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Km:</span>
                    <span className="text-white ml-2 font-['JetBrains_Mono']">{(selectedAcceptance.vehicle_km || 0).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Carburante:</span>
                    <span className="text-white ml-2">{selectedAcceptance.fuel_level}</span>
                  </div>
                </div>
              </div>

              {/* Damage Data Section - New unified damage marker */}
              {selectedAcceptance.damage_data?.marks?.length > 0 && (
                <div className="border-t border-zinc-800 pt-4">
                  <h4 className="text-primary uppercase text-xs font-bold mb-3 flex items-center gap-2">
                    <CarFront className="w-4 h-4" /> Danni Carrozzeria Registrati
                  </h4>
                  {/* Hidden VehicleDamageMarker for PDF generation */}
                  <div className="max-w-md mx-auto">
                    <VehicleDamageMarker 
                      damageData={selectedAcceptance.damage_data} 
                      setDamageData={() => {}} 
                      canvasRef={viewDamageCanvasRef}
                      readOnly={true}
                    />
                  </div>
                  <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2 mt-2">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-red-400">
                        {selectedAcceptance.damage_data.marks.filter(m => m.type === "circle").length} Ammaccature
                      </span>
                      <span className="text-orange-400">
                        {selectedAcceptance.damage_data.marks.filter(m => m.type === "x").length} Graffi
                      </span>
                    </div>
                    <div className="text-zinc-300 text-sm">
                      {getDamageDescription(selectedAcceptance.damage_data.marks)}
                    </div>
                  </div>
                </div>
              )}

              {/* Legacy Damage Marks Section - for backwards compatibility */}
              {selectedAcceptance.damage_marks && !selectedAcceptance.damage_data?.marks?.length && (selectedAcceptance.damage_marks.left?.length > 0 || selectedAcceptance.damage_marks.right?.length > 0) && (
                <div className="border-t border-zinc-800 pt-4">
                  <h4 className="text-primary uppercase text-xs font-bold mb-3 flex items-center gap-2">
                    <CarFront className="w-4 h-4" /> Mappa Danni Registrati
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Lato Sinistro</p>
                      <p className="text-white">
                        {selectedAcceptance.damage_marks.left?.filter(m => m.type === "circle").length || 0} ammaccature,{" "}
                        {selectedAcceptance.damage_marks.left?.filter(m => m.type === "x").length || 0} graffi
                      </p>
                    </div>
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Lato Destro</p>
                      <p className="text-white">
                        {selectedAcceptance.damage_marks.right?.filter(m => m.type === "circle").length || 0} ammaccature,{" "}
                        {selectedAcceptance.damage_marks.right?.filter(m => m.type === "x").length || 0} graffi
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t border-zinc-800 pt-4">
                <h4 className="text-primary uppercase text-xs font-bold mb-2">Problemi Segnalati</h4>
                <p className="text-white">{selectedAcceptance.reported_issues}</p>
              </div>

              {selectedAcceptance.accessories?.length > 0 && (
                <div className="border-t border-zinc-800 pt-4">
                  <h4 className="text-primary uppercase text-xs font-bold mb-2">Accessori Presenti</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedAcceptance.accessories.map((acc) => (
                      <span key={acc} className="px-2 py-1 bg-zinc-800 rounded-sm text-xs text-zinc-300">
                        {acc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Import from Appointment Dialog */}
      {showImportDialog && (
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Importa da Appuntamento
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-zinc-400 text-sm mb-4">
                Seleziona un appuntamento di oggi per importare i dati cliente e veicolo nella scheda di accettazione.
              </p>
              {appointments.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  Nessun appuntamento per oggi
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {appointments.map((apt) => (
                    <div 
                      key={apt.appointment_id}
                      className="bg-zinc-800 p-4 rounded border border-zinc-700 hover:border-primary cursor-pointer transition-colors"
                      onClick={() => importFromAppointment(apt)}
                      data-testid={`import-apt-${apt.appointment_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-primary font-['JetBrains_Mono'] text-sm">
                              {apt.appointment_time}
                            </span>
                            <span className="text-white font-semibold">
                              {apt.customer_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-zinc-400 text-sm">
                            <span className="flex items-center gap-1">
                              <Car className="w-3 h-3" />
                              {apt.vehicle_plate}
                            </span>
                            <span>
                              {apt.vehicle_brand} {apt.vehicle_model}
                            </span>
                            <span className="bg-zinc-700 px-2 py-0.5 rounded text-xs">
                              {apt.service_type}
                            </span>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-primary" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowImportDialog(false)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
